import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Globe, CheckCircle, CircleNotch, Copy, Trash, ArrowClockwise, Plugs, Info } from "@phosphor-icons/react";

const inputCls = "bg-surface-2 border-border text-fg";

const STATUS = {
  verified: { label: "Vérifié", cls: "bg-accent/15 text-accent border-accent/40" },
  pending: { label: "Vérification en cours", cls: "bg-amber-400/10 text-amber-500 border-amber-400/40" },
  not_started: { label: "En attente des DNS", cls: "bg-surface-2 text-muted border-border" },
  failed: { label: "Échec — vérifiez les DNS", cls: "bg-destructive/10 text-destructive border-destructive/40" },
  temporary_failure: { label: "Échec temporaire", cls: "bg-destructive/10 text-destructive border-destructive/40" },
};

const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copié"); };

function DnsRecords({ domain }) {
  return (
    <div className="rounded-md border border-border overflow-x-auto" data-testid="sender-domain-records">
      <table className="w-full text-xs">
        <thead className="bg-surface-2/60 text-muted">
          <tr><th className="text-left p-2">Type</th><th className="text-left p-2">Nom (hôte)</th><th className="text-left p-2">Valeur</th><th className="text-left p-2">Prio.</th><th className="p-2" /></tr>
        </thead>
        <tbody>
          {domain.records.map((r, i) => (
            <tr key={i} className="border-t border-border/60 align-top" data-testid={`sender-domain-record-${i}`}>
              <td className="p-2 font-mono text-fg">{r.type}</td>
              <td className="p-2 font-mono text-fg break-all">{r.name}</td>
              <td className="p-2 font-mono text-muted break-all max-w-[220px]">{r.value}</td>
              <td className="p-2 font-mono text-muted">{r.priority ?? "—"}</td>
              <td className="p-2 whitespace-nowrap">
                <button onClick={() => copy(r.value)} data-testid={`sender-domain-copy-${i}`} title="Copier la valeur"
                  className="text-muted hover:text-accent transition-colors"><Copy size={14} /></button>
                {r.status === "verified" && <CheckCircle size={14} weight="fill" className="text-accent inline ml-1.5" />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ConnectAddress({ domain, onConnected }) {
  const [local, setLocal] = useState("");
  const [fromName, setFromName] = useState("");
  const [saving, setSaving] = useState(false);
  const connect = async () => {
    if (!local.trim()) { toast.error("Saisissez votre adresse (partie avant le @)."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/crm/email-account/connect-domain", { email: `${local.trim()}@${domain.name}`, from_name: fromName.trim() });
      toast.success("Adresse connectée ✅ — vos emails partiront de votre domaine.");
      onConnected(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-3" data-testid="sender-domain-connect">
      <div>
        <label className="overline block mb-1.5">Votre adresse d'envoi</label>
        <div className="flex items-center gap-2">
          <Input className={inputCls} value={local} onChange={(e) => setLocal(e.target.value)} placeholder="prenom.nom" data-testid="sender-domain-local" />
          <span className="text-sm text-muted font-mono shrink-0">@{domain.name}</span>
        </div>
      </div>
      <div>
        <label className="overline block mb-1.5">Nom affiché (optionnel)</label>
        <Input className={inputCls} value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Paul Martin" data-testid="sender-domain-from-name" />
      </div>
      <button onClick={connect} disabled={saving} data-testid="sender-domain-connect-btn"
        className="flex items-center gap-2 bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift disabled:opacity-60 w-full justify-center">
        <Plugs size={16} weight="bold" /> {saving ? "Connexion…" : "Connecter cette adresse"}
      </button>
    </div>
  );
}

export const DomainSender = ({ onConnected }) => {
  const [state, setState] = useState(undefined);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/crm/sender-domain"); setState(data); }
    catch { setState({ domain: null, enabled: false }); }
  };
  useEffect(() => { load(); }, []);

  const run = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); setState((s) => ({ ...s, domain: data.domain ?? null })); if (ok) toast.success(ok(data)); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const add = () => name.trim() ? run(() => api.post("/crm/sender-domain", { domain: name.trim() }), () => "Domaine ajouté — ajoutez maintenant les enregistrements DNS.") : toast.error("Saisissez votre nom de domaine.");
  const verify = () => run(() => api.post("/crm/sender-domain/verify"), (d) => d.domain?.status === "verified" ? "Domaine vérifié ✅" : "Vérification lancée — la propagation DNS peut prendre quelques minutes.");
  const remove = () => run(() => api.delete("/crm/sender-domain").then(() => ({ data: { domain: null } })), () => "Domaine supprimé.");

  if (state === undefined) return <div className="text-muted text-sm animate-pulse">Chargement…</div>;
  const d = state.domain;
  const st = d ? (STATUS[d.status] || STATUS.not_started) : null;

  return (
    <div className="space-y-4" data-testid="sender-domain-panel">
      <div className="rounded-md border border-accent/25 bg-accent/[0.05] p-3 text-xs text-muted flex gap-2">
        <Info size={15} className="text-accent shrink-0 mt-0.5" />
        <span>Aucun mot de passe requis : vous autorisez interKO à envoyer pour <b>votre nom de domaine</b> en ajoutant quelques enregistrements DNS. Votre messagerie actuelle (Google Workspace, Microsoft 365…) n'est <b>pas modifiée</b> et les réponses arrivent toujours dans votre boîte habituelle.</span>
      </div>

      {!state.enabled && (
        <p className="text-xs text-amber-500" data-testid="sender-domain-disabled">L'envoi via votre nom de domaine est en cours d'activation sur interKO.</p>
      )}

      {!d ? (
        <div className="space-y-2">
          <label className="overline block">Nom de domaine de votre entreprise</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Globe size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <Input className={`${inputCls} pl-9`} value={name} onChange={(e) => setName(e.target.value)} placeholder="mon-esn.fr" data-testid="sender-domain-input"
                onKeyDown={(e) => e.key === "Enter" && add()} />
            </div>
            <button onClick={add} disabled={busy || !state.enabled} data-testid="sender-domain-add-btn"
              className="bg-accent text-black font-semibold px-4 rounded-md hover-lift disabled:opacity-60 text-sm shrink-0">
              {busy ? <CircleNotch size={16} className="animate-spin" /> : "Ajouter"}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Globe size={16} className="text-accent" />
              <span className="font-mono text-sm text-fg" data-testid="sender-domain-name">{d.name}</span>
              <span className={`text-[11px] border rounded-full px-2 py-0.5 ${st.cls}`} data-testid="sender-domain-status">{st.label}</span>
            </div>
            <button onClick={remove} disabled={busy} data-testid="sender-domain-delete-btn"
              className="flex items-center gap-1.5 text-xs text-muted hover:text-destructive transition-colors"><Trash size={13} /> Supprimer</button>
          </div>

          {d.status !== "verified" ? (
            <>
              <p className="text-xs text-muted">Ajoutez ces enregistrements dans la zone DNS de <b>{d.name}</b> (OVH, Gandi, Ionos, Cloudflare — proxy désactivé…), puis cliquez sur « Vérifier ». Si un enregistrement SPF existe déjà sur le même nom, fusionnez-les.</p>
              <DnsRecords domain={d} />
              <button onClick={verify} disabled={busy} data-testid="sender-domain-verify-btn"
                className="flex items-center gap-2 text-sm border border-accent/40 text-accent rounded-md px-4 py-2 hover:bg-accent/10 disabled:opacity-60 transition-colors">
                <ArrowClockwise size={14} className={busy ? "animate-spin" : ""} /> {busy ? "Vérification…" : "Vérifier maintenant"}
              </button>
            </>
          ) : (
            <ConnectAddress domain={d} onConnected={onConnected} />
          )}
        </div>
      )}
    </div>
  );
};
